import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { DataRoomFile } from '@server/modules/client-matter/common/types';
import { Workbook, Worksheet } from 'exceljs';
import { saveAs } from 'file-saver';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ExitIcon, ExportIcon, InviteUserIcon, OrganizeIcon, SearchIcon } from '@/assets/icons';
import { ROOT_FOLDER_PATH } from '@/common/constants';
import { DataRoomLoader } from '@/components/DataRoomLoader';
import { ErrorContent } from '@/components/ErrorContent';
import { SpreadsheetViewer } from '@/components/SpreadsheetViewer';
import {
  createFileInstanceId,
  type FileInstance,
} from '@/contexts/overview/dataroom/fileInstanceUtils';
import { useDataRoom } from '@/contexts/overview/dataroom/utils';
import { MarveriIcon } from '@/pages/overview/common/MarveriIcon';
import { ScrollableDiv } from '@/pages/overview/common/ScrollableDiv';
import { formatDate, formatFileDisplay, useOverview } from '@/pages/overview/common/utils';
import { DataRoomAddNew } from '@/pages/overview/dataroom/content/common/DataRoomAddNew';
import { DataRoomButton } from '@/pages/overview/dataroom/content/common/DataRoomButton';
import { DataRoomToolButton } from '@/pages/overview/dataroom/content/common/DataRoomToolButton';
import { DownloadButton } from '@/pages/overview/dataroom/content/common/DownloadButton';
import { DragOverlayContent } from '@/pages/overview/dataroom/content/common/DragOverlayContent';
import {
  buildFileMoves,
  determineSourceFolderForFile,
  type FolderPath,
  getFileAllPaths,
  getFileCurrentPath,
  getFolderDisplayName,
  isFileSelectable,
  isRootPath,
  isTrashPath,
} from '@/pages/overview/dataroom/content/common/folderUtils';
import { InviteUserModal } from '@/pages/overview/dataroom/content/common/InviteUserModal';
import { RedlineButton } from '@/pages/overview/dataroom/content/common/RedlineButton';
import { RenameOptions } from '@/pages/overview/dataroom/content/common/RenameOptions';
import { showMoveToast } from '@/pages/overview/dataroom/content/common/toast-utils';
import { FilesByPath } from '@/pages/overview/dataroom/content/common/types';
import { OrganizedView } from '@/pages/overview/dataroom/content/OrganizedView';
import { PdfViewer } from '@/pages/pdf-viewer/PdfViewer';
import { showErrorToast, showSuccessToast } from '@/utils/toasts/Toasts';
import { trpcClient, trpcReact } from '@/utils/trpc';

export const DataRoomContent = () => {
  const featureFlags = useFlags();
  const {
    matter: {
      number: matterNumber,
      name: matterName,
      client: { number: clientNumber, name: clientName },
      fileUploadPercentComplete,
      fileUploadErrored,
      tools,
      users,
      id: clientMatterId,
      currentEditor,
      createdBy,
      filesDirectory,
      lookupKey,
      folders: allFolders,
    },
    getToolProcessing,
    isUserCurrentEditor,
    user,
    isPanelCollapsed,
  } = useOverview();

  const {
    isViewerOpen,
    setIsViewerOpen,
    isSpreadsheetViewerOpen,
    setIsSpreadsheetViewerOpen,
    setShouldFallbackToPdf,
    setSelectedFile,
    selectedFile,
    setDraggedContent,
    selectedFileUrl,
    selectedFileOriginalUrl,
    setIsCheckAllActive,
    isCheckAllActive,
    checkedFiles,
    setCheckedFiles,
    checkedFileInstances,
    setCheckedFileInstances,
    goToNextDocument,
    goToPreviousDocument,
    dataRoomFiles,
    filteredDataRoomFiles,
    setFilteredDataRoomFiles,
    hasCheckedFiles,
    updateRenameFormStatus,
    updateAllFolderSelection,
    selectSecondFileByName,
    secondSelectedFile,
    secondSelectedFileUrl,
    setIsFolderOpenByPath,
    newlyCreatedFolder,
    setNewlyCreatedFolder,
    isFileRenameActive,
    selectedFoldersArray,
    pathOrganizedDocuments,
    foldersByPaths,
    setIsFolderChecked,
  } = useDataRoom();

  const [organizeLoading, setOrganizeLoading] = useState(false);
  const [isRenameClientNameActive, setIsRenameClientNameActive] = useState(false);
  const [newClientName, setNewClientName] = useState(matterName);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [draggedFileContext, setDraggedFileContext] = useState<{
    fileId: string;
    folderPath: string;
  } | null>(null);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredSelectedFoldersArray = useMemo(() => {
    const fileFolders = filteredDataRoomFiles.map((file) => {
      const currentPath = getFileCurrentPath(file, foldersByPaths);
      return currentPath.substring(0, currentPath.lastIndexOf('/'));
    });
    return selectedFoldersArray.filter((folder) => fileFolders.includes(folder));
  }, [filteredDataRoomFiles, selectedFoldersArray, foldersByPaths]);

  const clientNameToUpdate = trpcReact.clientMatter.updateName.useMutation();

  // Mutations for drag operations with toast notifications
  const dragMoveFiles = trpcReact.dataRoom.bulkMoveFilesToFolder.useMutation({
    onSuccess: (data, variables) => {
      const targetFolder = allFolders.find((folder) => folder.key === variables.targetFolderKey);
      const targetFolderName =
        targetFolder?.displayName || targetFolder?.name || 'destination folder';

      showMoveToast(data, variables.targetFolderKey, targetFolderName);
    },
    onError: (error) => {
      showErrorToast(`Failed to move files: ${error.message}`);
    },
  });

  const dragMoveFolder = trpcReact.dataRoom.updateFolderParent.useMutation({
    onSuccess: (data, variables) => {
      const targetFolder = allFolders.find((folder) => folder.key === variables.newParentKey);
      const sourceFolder = allFolders.find((folder) => folder.key === variables.folderKey);
      const targetFolderName =
        targetFolder?.displayName || targetFolder?.name || 'destination folder';
      const sourceFolderName = sourceFolder?.displayName || sourceFolder?.name || 'folder';

      showSuccessToast(`Successfully moved ${sourceFolderName} to ${targetFolderName}.`);
    },
    onError: (error) => {
      showErrorToast(`Failed to move folder: ${error.message}`);
    },
  });
  const updateClientName = useCallback(
    (newName: string | null) => {
      if (newName !== null && newName !== matterName) {
        clientNameToUpdate.mutate({
          number: matterNumber,
          clientNumber: clientNumber,
          clientMatterId: clientMatterId,
          name: newName,
        });
      }

      setIsRenameClientNameActive(false);
    },
    [clientMatterId, clientNameToUpdate, clientNumber, matterName, matterNumber],
  );

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      {
        const fileListComponent = document.getElementById('organized-view-component');
        const dataRoomButtons = document.querySelector('[data-testid="data-room-buttons"]');
        const headerCheckBox = document.querySelector('[data-testid="data-room-header-checkbox"]');
        const dataRoomSidebar = document.querySelector('[data-testid="right-panel-sidebar"]');
        const pdfViewer = document.querySelector('[data-testid="pdf-viewer"]');
        const spreadsheetViewer = document.querySelector('[data-testid="spreadsheet-viewer"]');
        const inviteButton = document.getElementById('invite-button');
        const inviteModal = document.querySelector('[data-testid="invite-user-modal"]');
        // TODO: Refactor to remove this selector. This design is unintuitive.
        const clientMatterNameContainer = document.querySelector(
          '[data-testid="rename-client-name-form"]',
        );
        const clientMatterSidebar = document.querySelector('[data-testid="client-matter-sidebar"]');
        if (
          fileListComponent?.contains(event.target as Node) ||
          inviteButton?.contains(event.target as Node) ||
          inviteModal?.contains(event.target as Node) ||
          dataRoomButtons?.contains(event.target as Node) ||
          headerCheckBox?.contains(event.target as Node) ||
          dataRoomSidebar?.contains(event.target as Node) ||
          pdfViewer?.contains(event.target as Node) ||
          spreadsheetViewer?.contains(event.target as Node) ||
          clientMatterNameContainer?.contains(event.target as Node) ||
          clientMatterSidebar?.contains(event.target as Node)
        )
          return;
        else {
          updateClientName(newClientName);
          setSelectedFile('');
          updateRenameFormStatus(false);
          setIsRenameClientNameActive(false);
          localStorage.removeItem('document-list-side-panel-tab');
          localStorage.removeItem('document-list-file-id');
        }
      }
    },
    [newClientName, setSelectedFile, updateClientName, updateRenameFormStatus],
  );

  useEffect(() => {
    if (isFileRenameActive) return;
    else {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [
    checkedFileInstances.length,
    handleClickOutside,
    isFileRenameActive,
    setSelectedFile,
    updateRenameFormStatus,
  ]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const hasRenameDocumentSetBeenCalled = localStorage.getItem('renameDocumentSetActive');

    if (hasRenameDocumentSetBeenCalled === 'false') {
      setIsRenameClientNameActive(true);
      localStorage.setItem('renameDocumentSetActive', 'true');
    }
  }, []);

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value;
      setSearchQuery(searchValue);
      let files = [] as DataRoomFile[];
      if (searchValue.trim()) {
        const searchTerms = searchValue
          .toLowerCase()
          .split(' ')
          .filter((term) => term);

        files = dataRoomFiles.filter((fileExtension) => {
          const displayName = fileExtension.displayName.toLowerCase();
          const summaryText = fileExtension.summary?.toLowerCase();

          return searchTerms.every(
            (term) => displayName.includes(term) || summaryText?.includes(term),
          );
        });
        setIsSearchActive(true);
      }

      const uniqueFileIds = new Set(checkedFiles.map((file) => file.id));
      files = [...checkedFiles, ...files.filter((file) => !uniqueFileIds.has(file.id))];

      if (searchValue.trim() === '') {
        setSearchQuery('');
        setFilteredDataRoomFiles(dataRoomFiles);
        setIsSearchActive(false);
      } else {
        setFilteredDataRoomFiles(files);
      }
    },
    [checkedFiles, dataRoomFiles, setFilteredDataRoomFiles],
  );

  const handleCancelSearch = () => {
    setSearchQuery('');
    setFilteredDataRoomFiles(dataRoomFiles);
    setIsSearchActive(false);
  };

  const allDocumentsSelected = useMemo(
    () => dataRoomFiles.length === checkedFiles.length,
    [checkedFiles.length, dataRoomFiles.length],
  );

  const dataRoomDownload = useMemo(() => {
    if (!allDocumentsSelected) {
      // Create a new object with only the checked files
      const filteredFiles: FilesByPath = {};
      const checkedFileIds = new Set(checkedFiles.map((file) => file.id));

      // Iterate through each path and its files
      pathOrganizedDocuments.forEach((files, path) => {
        const filteredFilesInPath = files.filter((file) => checkedFileIds.has(file.id));
        if (filteredFilesInPath.length > 0) {
          filteredFiles[path] = filteredFilesInPath;
        }
      });

      return filteredFiles;
    } else {
      const allFiles = Object.fromEntries(pathOrganizedDocuments);
      return allFiles;
    }
  }, [allDocumentsSelected, checkedFiles, pathOrganizedDocuments]);

  const dataRoomFileNames = useMemo(() => {
    return dataRoomFiles.map((file) => file.name);
  }, [dataRoomFiles]);

  const setAllFilesChecked = useCallback(() => {
    setIsCheckAllActive((prevIsCheckAllActive) => {
      const newIsCheckAllActive = !prevIsCheckAllActive;
      if (newIsCheckAllActive) {
        const nonTrashDataRoomFiles = filteredDataRoomFiles.filter((file) =>
          isFileSelectable(file, foldersByPaths),
        );
        setCheckedFiles([...nonTrashDataRoomFiles]);

        // Create FileInstance objects for all files in all their non-trash locations
        const allFileInstances: FileInstance[] = [];
        nonTrashDataRoomFiles.forEach((file) => {
          const allPaths = getFileAllPaths(file, foldersByPaths);
          allPaths.forEach((filePath) => {
            // Extract folder path from file path
            const lastSlashIndex = filePath.lastIndexOf('/');
            const folderPath = filePath.substring(0, lastSlashIndex);

            // Only include non-trash locations
            if (!isTrashPath(folderPath as FolderPath)) {
              allFileInstances.push({
                file,
                folderPath,
                instanceId: createFileInstanceId(file.id, folderPath),
              });
            }
          });
        });
        setCheckedFileInstances(allFileInstances);

        // Select all folders
        setIsFolderChecked((prevState) => {
          const newState = { ...prevState };
          // Set all folder paths to true
          Array.from(foldersByPaths.keys()).forEach((path) => {
            if (!path.startsWith('/root/~trash')) {
              newState[path] = true;
            }
          });
          return newState;
        });
      } else {
        setCheckedFiles([]);
        setCheckedFileInstances([]);
        // Unselect all folders
        setIsFolderChecked({});
      }
      return newIsCheckAllActive;
    });
  }, [
    filteredDataRoomFiles,
    setCheckedFiles,
    setCheckedFileInstances,
    setIsCheckAllActive,
    foldersByPaths,
    setIsFolderChecked,
  ]);

  const noMatter = useMemo(() => {
    return !matterNumber;
  }, [matterNumber]);

  const redlinesAreProcessing = useMemo(() => {
    return !getToolProcessing('REDLINE').complete;
  }, [getToolProcessing]);

  const renameIsProcessing = useMemo(() => {
    return !getToolProcessing('RENAME').complete;
  }, [getToolProcessing]);

  const organizeIsProcessing = useMemo(() => {
    return !getToolProcessing('ORGANIZE').complete;
  }, [getToolProcessing]);

  const setAllFilesUnchecked = useCallback(() => {
    setCheckedFiles([]);
    setIsFolderChecked({});
    setIsCheckAllActive(false);
  }, [setCheckedFiles, setIsCheckAllActive, setIsFolderChecked]);

  const renameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isRenameClientNameActive) {
      if (renameInputRef && renameInputRef.current) {
        renameInputRef.current.focus();
      }
    }
  }, [isRenameClientNameActive, newClientName, renameInputRef]);

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 15,
    },
  });
  const mouseSensor = useSensor(MouseSensor);
  const touchSensor = useSensor(TouchSensor);
  const keyboardSensor = useSensor(KeyboardSensor);

  const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor, pointerSensor);

  const setUncheckedAfterDrag = useCallback(() => {
    setCheckedFiles([]);
  }, [setCheckedFiles]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (
        event.over &&
        event.active.data.current &&
        event.active.data.current.toString() != event.over.id.toString()
      ) {
        if (event.active.id.toString().startsWith('folder://')) {
          const oldPath = event.active.id.toString().slice(9);
          const newPath = event.over.id.toString();
          if (oldPath === newPath) return;

          const targetFolder = foldersByPaths.get(newPath)?.[0];
          if (!targetFolder) return;

          const sourceFolder = foldersByPaths.get(oldPath)?.[0];
          if (!sourceFolder) return;

          // Check if target folder is a subfolder of source folder
          const isSubfolder = (targetPath: string, sourcePath: string) => {
            // Get all parent paths of the target folder
            const targetPathParts = targetPath.split('/').filter(Boolean);
            const sourcePathParts = sourcePath.split('/').filter(Boolean);

            // If target path is shorter than source path, it can't be a subfolder
            if (targetPathParts.length <= sourcePathParts.length) return false;

            // Check if target path starts with source path
            return (
              targetPathParts.slice(0, sourcePathParts.length).join('/') ===
              sourcePathParts.join('/')
            );
          };

          if (isSubfolder(newPath, oldPath)) {
            showErrorToast('Cannot move a folder into its own subfolder.');
            return;
          }

          // Use the new updateFolderParent route for folder moves
          dragMoveFolder.mutate({
            clientMatterId,
            folderKey: sourceFolder.key,
            newParentKey: targetFolder.key,
          });
        } else {
          const newPath = event.over.id.toString();
          const targetFolder = foldersByPaths.get(newPath)?.[0];
          if (!targetFolder) return;

          // Build fileMoves array with source context
          const sourceContextMap = new Map<string, string>();

          // Add drag context for the specifically dragged file
          if (draggedFileContext && draggedFileContext.fileId) {
            sourceContextMap.set(draggedFileContext.fileId, draggedFileContext.folderPath);
          }

          // For other checked files, try to get source from checkedFileInstances
          checkedFileInstances.forEach((instance) => {
            if (!sourceContextMap.has(instance.file.id)) {
              sourceContextMap.set(instance.file.id, instance.folderPath);
            }
          });

          const fileMoves = buildFileMoves(checkedFiles, foldersByPaths, sourceContextMap);

          if (fileMoves.length > 0) {
            dragMoveFiles.mutate({
              clientMatterId,
              fileMoves,
              targetFolderKey: targetFolder.key,
              targetFolderType: 'default',
            });
          }
        }
      }

      // Clear drag context
      setDraggedFileContext(null);
      setUncheckedAfterDrag();
      updateAllFolderSelection();
    },
    [
      checkedFiles,
      checkedFileInstances,
      draggedFileContext,
      clientMatterId,
      setUncheckedAfterDrag,
      updateAllFolderSelection,
      foldersByPaths,
      dragMoveFiles,
      dragMoveFolder,
    ],
  );

  const handleDragStart = (event: DragStartEvent) => {
    const draggableId = event.active.id.toString();
    if (draggableId.startsWith('folder://')) {
      const folder = draggableId.slice(9);
      setDraggedContent(folder);

      const files = dataRoomFiles.filter((file) => {
        const currentPath = getFileCurrentPath(file, foldersByPaths);
        const folderPath = folder.endsWith('/') ? folder : `${folder}/`;
        return currentPath === folder || currentPath.startsWith(folderPath);
      });
      setCheckedFiles(files);
      setIsFolderChecked(() => {
        const newState = { [folder]: true };
        return newState;
      });
    } else {
      // Extract fileId and folderPath from instance ID (format: "fileId:folderPath")
      const [fileId, folderPath] = draggableId.includes(':')
        ? draggableId.split(':')
        : [draggableId, null];

      setDraggedContent('');
      setDraggedFileContext({ fileId, folderPath: folderPath || '' });

      if (!checkedFiles.some((checkedFile) => checkedFile.id === fileId)) {
        const file = dataRoomFiles.find((file) => file.id === fileId);
        if (file) {
          setCheckedFiles([...checkedFiles, file]);
        }
      }
    }
  };

  const createFolder = trpcReact.dataRoom.createFolder.useMutation();

  const organizeCheckedFiles = useCallback(async () => {
    setOrganizeLoading(true);

    // Track folders that had files before the operation
    const foldersWithFilesBefore = new Set<string>();
    for (const file of checkedFiles) {
      const currentPath = getFileCurrentPath(file, foldersByPaths);
      const folderPath = currentPath.substring(0, currentPath.lastIndexOf('/'));
      // Skip root folder and trash folder
      if (folderPath !== ROOT_FOLDER_PATH && !folderPath.startsWith(`${ROOT_FOLDER_PATH}/~trash`)) {
        foldersWithFilesBefore.add(folderPath);
      }
    }

    // Group files by practice area
    const filesByPracticeArea = checkedFiles.reduce(
      (acc, file) => {
        const practiceArea = file.practiceArea || 'Uncategorized';
        if (!acc[practiceArea]) {
          acc[practiceArea] = [];
        }
        acc[practiceArea].push(file);
        return acc;
      },
      {} as Record<string, DataRoomFile[]>,
    );

    // Create folders and move files
    const movedFileNames = new Set<string>();
    for (const [practiceArea, files] of Object.entries(filesByPracticeArea)) {
      // Check if folder already exists by looking for matching key
      const folderKey = practiceArea.toLowerCase().replace(/[^a-z0-9]/g, '_');
      const existingFolder = Array.from(foldersByPaths.values())
        .flat()
        .find((folder) => folder.key === folderKey);

      if (!existingFolder) {
        // Create the folder if it doesn't exist
        await createFolder.mutateAsync({
          clientMatterId,
          name: practiceArea,
          type: 'default',
        });
      }

      // Move files to the folder with source context
      const fileMoves = files.map((file) => {
        // Determine source folder for each file
        const sourceFolderKey = determineSourceFolderForFile(file, foldersByPaths);
        return {
          fileName: file.name,
          sourceFolderKey,
        };
      });

      // Track which files we're moving
      files.forEach((file) => movedFileNames.add(file.name));
      await trpcClient.dataRoom.bulkMoveFilesToFolder.mutate({
        clientMatterId,
        fileMoves,
        targetFolderKey: folderKey,
        targetFolderType: 'default',
      });

      // Update folder open state
      setIsFolderOpenByPath((map) => {
        const newMap = { ...map };
        newMap[practiceArea] = true;
        newMap['/'] = true;
        const storedOpenMapJson = JSON.stringify(newMap);
        localStorage.setItem(`folderOpenMapRefactor-${clientMatterId}`, storedOpenMapJson);
        return newMap;
      });
    }

    // Sort folders by path length in descending order (deepest first)
    const sortedFoldersToCheck = Array.from(foldersWithFilesBefore).sort((a, b) => {
      // Count the number of path segments (excluding root)
      const aDepth = a.split('/').filter(Boolean).length;
      const bDepth = b.split('/').filter(Boolean).length;
      return bDepth - aDepth; // Descending order
    });

    // Keep track of deleted folders to properly check for remaining subfolders
    const deletedFolders = new Set<string>();

    // Check and delete folders from deepest to shallowest, recursively
    const deleteEmptyFoldersRecursively = async (folderPath: string) => {
      const folder = foldersByPaths.get(folderPath)?.[0];
      if (!folder || deletedFolders.has(folderPath)) return;

      const hasFiles = dataRoomFiles.some((file) => {
        if (movedFileNames.has(file.name)) return false;
        const filePath = getFileCurrentPath(file, foldersByPaths);
        return (
          filePath.startsWith(folderPath + '/') &&
          !filePath.slice(folderPath.length + 1).includes('/')
        );
      });

      const hasSubfolders = Array.from(foldersByPaths.keys()).some(
        (path) =>
          path !== folderPath && path.startsWith(folderPath + '/') && !deletedFolders.has(path),
      );

      if (!hasFiles && !hasSubfolders) {
        await trpcClient.dataRoom.deleteFolder.mutate({
          clientMatterId,
          folderKey: folder.key,
        });
        deletedFolders.add(folderPath);

        // Recursively attempt to delete the parent
        const parentPath = folderPath.slice(0, folderPath.lastIndexOf('/'));
        if (parentPath && parentPath !== ROOT_FOLDER_PATH) {
          await deleteEmptyFoldersRecursively(parentPath);
        }
      }
    };

    try {
      for (const folderPath of sortedFoldersToCheck) {
        await deleteEmptyFoldersRecursively(folderPath);
      }
    } catch (error) {
      // Continue execution to ensure we clean up UI state
    } finally {
      setAllFilesUnchecked();
      setOrganizeLoading(false);
    }
  }, [
    checkedFiles,
    clientMatterId,
    createFolder,
    setAllFilesUnchecked,
    setIsFolderOpenByPath,
    foldersByPaths,
    dataRoomFiles,
  ]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (event.key === 'Escape') {
      setIsRenameClientNameActive(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewClientName(e.target.value);
  };

  const reprocessMutation = trpcReact.dataRoom.reprocessDataroom.useMutation();
  const debugReprocessDataroom = useCallback(() => {
    reprocessMutation.mutate({
      clientMatterNumber: matterNumber,
      clientNumber: clientNumber,
      clientMatterId: clientMatterId,
    });
  }, [clientMatterId, clientNumber, matterNumber, reprocessMutation]);

  const handleSpreadsheetFallbackToPdf = useCallback(() => {
    setShouldFallbackToPdf(true);
    setIsSpreadsheetViewerOpen(false);
    setIsViewerOpen(true);
  }, [setShouldFallbackToPdf, setIsSpreadsheetViewerOpen, setIsViewerOpen]);

  // Reset fallback state when a new file is selected
  useEffect(() => {
    setShouldFallbackToPdf(false);
  }, [selectedFile, setShouldFallbackToPdf]);

  const userSelectedTool = tools.map((tool) => tool.toolType);

  const DataRoomContentDisplay = () => {
    if (fileUploadErrored) {
      return <ErrorContent />;
    }
    if (noMatter || dataRoomFiles?.length === 0) {
      return (
        <DataRoomLoader fileUploadPercent={fileUploadPercentComplete} loaderFocus="Documents" />
      );
    }
    return (
      <div id="organized-view-component">
        <OrganizedView />
      </div>
    );
  };

  const downloadAllFilesAsExcel = useCallback(async () => {
    const fetchExcelTemplate = async () => {
      const response = await fetch('/File Index Draft Template.xlsx');
      return response.arrayBuffer();
    };

    const setCellValue = (sheet: Worksheet, cell: string, value: string) => {
      sheet.getCell(cell).value = value;
    };

    // Helper function to get folder depth
    const getFolderDepth = (path: string) => {
      // Split path and remove empty strings and 'root'
      const parts = path.split('/').filter((part) => part && part !== 'root');
      // Subtract 1 because the first level after root should have depth 0
      return Math.max(0, parts.length - 1);
    };

    // Get all folders and files in hierarchical order
    const getHierarchicalItems = () => {
      const items: Array<{
        type: 'folder' | 'file';
        name: string;
        path: string;
        data?: DataRoomFile;
        depth: number;
      }> = [];

      // Sort paths to ensure parent folders come before their children
      // Use the same sorting as OrganizedView to ensure consistent order
      const sortedPaths = Array.from(foldersByPaths.keys())
        .filter((path) => !path.startsWith('/root/~trash'))
        .sort((a, b) => {
          // Use the same sorting as OrganizedView to ensure consistent order
          const newA = a.replaceAll('/', ' /');
          const newB = b.replaceAll('/', ' /');
          if (newA < newB) {
            return -1;
          } else if (newA > newB) {
            return 1;
          }
          return 0;
        }) as FolderPath[];

      // Add folders first
      for (const path of sortedPaths) {
        if (isRootPath(path)) continue; // Skip root folder
        const folder = foldersByPaths.get(path)?.[0];
        if (folder) {
          items.push({
            type: 'folder',
            name: getFolderDisplayName(path, foldersByPaths),
            path,
            depth: getFolderDepth(path),
          });
        }
      }

      // Add files under their respective folders - include linked files in all locations
      for (const file of dataRoomFiles) {
        const allPaths = getFileAllPaths(file, foldersByPaths); // Get ALL paths where file exists

        for (const filePath of allPaths) {
          // Skip files in trash folder
          if (!filePath.startsWith('/root/~trash')) {
            // Extract folder path from file path
            const lastSlashIndex = filePath.lastIndexOf('/');
            const folderPath = filePath.substring(0, lastSlashIndex);

            items.push({
              type: 'file',
              name: file.displayName || file.name,
              path: folderPath, // Use the specific folder path for this instance
              data: file,
              depth: isRootPath(folderPath as FolderPath)
                ? getFolderDepth(folderPath) // Root files at same level as root folders
                : getFolderDepth(folderPath) + 1, // Other files one level deeper than their folder
            });
          }
        }
      }

      // Sort items by path to maintain hierarchy
      // Use the same sorting as OrganizedView to ensure consistent order
      return items.sort((a, b) => {
        const newA = a.path.replaceAll('/', ' /');
        const newB = b.path.replaceAll('/', ' /');
        if (newA < newB) {
          return -1;
        } else if (newA > newB) {
          return 1;
        }
        return 0;
      });
    };

    const hasDataForColumn = (column: string) => {
      return dataRoomFiles.some((file) => {
        const title = formatFileDisplay(file, user?.namingTemplate);
        switch (column) {
          case 'Smart Title':
            return !!title && title !== 'Unknown.pdf';
          case 'Effective Date':
            return !!file.date;
          case 'Company':
            return !!file.company;
          case 'Counterparties':
            return file.subject && file.subject.length > 0;
          case 'Summary':
            return !!file.summary;
          default:
            return true;
        }
      });
    };

    const getColumnDefinitions = () => {
      const baseColumns = [
        { name: 'Current File Name', filterButton: false },
        { name: 'Type', filterButton: false },
        { name: 'Date Uploaded', filterButton: false },
        { name: 'Original File Name', filterButton: false },
      ];

      const optionalColumns = [
        { name: 'Smart Title', filterButton: false },
        { name: 'Effective Date', filterButton: false },
        { name: 'Company', filterButton: false },
        { name: 'Counterparties', filterButton: false },
        { name: 'Summary', filterButton: false },
      ];

      return [...baseColumns, ...optionalColumns.filter((col) => hasDataForColumn(col.name))];
    };

    const getRowData = (
      item: {
        type: 'folder' | 'file';
        name: string;
        path: string;
        data?: DataRoomFile;
        depth: number;
      },
      column: string,
    ) => {
      const indent = '  '.repeat(item.depth);

      if (column === 'Current File Name') {
        return indent + item.name;
      }
      if (column === 'Type') {
        return item.type === 'folder' ? 'Folder' : 'File';
      }
      if (!item.data) return ''; // Return empty for folders on other columns

      if (column === 'Date Uploaded') return formatDate(item.data.createdAt, 'Month DD, YYYY');
      if (column === 'Original File Name') return item.data.originalPathname.split('/').pop();
      if (column === 'Smart Title') return formatFileDisplay(item.data, user?.namingTemplate);
      if (column === 'Effective Date') return formatDate(item.data.date, 'Month DD, YYYY');
      if (column === 'Company') return item.data.company;
      if (column === 'Counterparties') return item.data.subject.join(', ');
      if (column === 'Summary') return item.data.summary;
      return '';
    };

    const arrayBuffer = await fetchExcelTemplate();
    const workbook = new Workbook();
    await workbook.xlsx.load(arrayBuffer);

    const sheet = workbook.worksheets[0];

    // These are the cells in the template that need this data
    setCellValue(sheet, 'C2', `${matterName}`);
    setCellValue(sheet, 'C3', new Date().toDateString());

    const tableColumns = getColumnDefinitions();
    const headerStyle = sheet.getCell('A5').style;

    // Get all items in hierarchical order
    const hierarchicalItems = getHierarchicalItems();

    // Add header row
    const headerRow = sheet.getRow(5);
    tableColumns.forEach((column, index) => {
      const cell = headerRow.getCell(index + 1);
      cell.value = column.name;
    });

    // Add data rows
    hierarchicalItems.forEach((item, rowIndex) => {
      const row = sheet.getRow(rowIndex + 6); // Start from row 6 (after header)
      tableColumns.forEach((column, colIndex) => {
        const cell = row.getCell(colIndex + 1);
        cell.value = getRowData(item, column.name);
      });
    });

    // Apply header style to all column headers
    const headerRowNum = 5;
    tableColumns.forEach((_, index) => {
      const columnLetter = String.fromCharCode(65 + index);
      const headerCell = sheet.getCell(`${columnLetter}${headerRowNum}`);
      headerCell.style = headerStyle;
    });

    // B7 is the first cell in the template we can use to derive consistent styling
    const templateStyle = sheet.getCell('B7').style;

    // Apply cell styles to all columns that have data
    tableColumns.forEach((_, index) => {
      const columnLetter = String.fromCharCode(65 + index);
      const column = sheet.getColumn(columnLetter);
      column.width = 15; // Default width
      column.eachCell({ includeEmpty: false }, (cell, rowNumber) => {
        const cellValue = cell.value?.toString() || '';
        const cellWidth = Math.max(15, Math.min(100, cellValue.length + 2));
        column.width = Math.max(column.width || 15, cellWidth);

        const border_color = 'FFD0D0D0';

        // Row 6 is where the table data
        // Get the item for this row
        const item = hierarchicalItems[rowNumber - 6]; // Subtract header row and 1-based index
        if (rowNumber >= 6) {
          // Only apply styling to table rows (after header)
          if (item?.type === 'folder') {
            // Style for folder rows
            cell.style = {
              ...templateStyle,
              font: {
                ...templateStyle.font,
                bold: true,
                color: { argb: 'FF2A426D' }, // Blue text
              },
              fill: {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFDCEBF3' }, // Light blue background
              },
              border: {
                top: { style: 'thin', color: { argb: border_color } },
                left: { style: 'thin', color: { argb: border_color } },
                bottom: { style: 'thin', color: { argb: border_color } },
                right: { style: 'thin', color: { argb: border_color } },
              },
            };
          } else {
            // Style for file rows
            cell.style = {
              ...templateStyle,
              border: {
                top: { style: 'thin', color: { argb: border_color } },
                left: { style: 'thin', color: { argb: border_color } },
                bottom: { style: 'thin', color: { argb: border_color } },
                right: { style: 'thin', color: { argb: border_color } },
              },
            };
          }
        }

        // Set row height and alignment for all rows
        const row = sheet.getRow(rowNumber);
        cell.alignment = {
          ...cell.alignment,
          vertical: 'middle',
          wrapText: false,
        };
        if (rowNumber >= 6) {
          // Only apply to table rows (after header)
          // Get the cell value from column A (Current File Name)
          const cellValue = sheet.getCell(`A${rowNumber}`).value?.toString() || '';
          // Calculate depth based on leading spaces divided by 2 (since we use 2 spaces per level)
          const leadingSpaces = cellValue.match(/^\s*/)?.[0].length || 0;
          const depth = Math.floor(leadingSpaces / 2);
          // Set outline level (1-based, where 1 is the top level)
          row.outlineLevel = Math.min(Math.max(1, depth), 7);
        }
        if (rowNumber >= 2) {
          row.height = 17;
        }
      });

      // Ensure column is visible
      column.hidden = false;
    });
    sheet.properties.outlineLevelRow = 7;
    sheet.properties.outlineProperties = { summaryBelow: false, summaryRight: false };

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `${new Date().toDateString()} ${matterName} File Export.xlsx`);
  }, [matterName, user?.namingTemplate, dataRoomFiles, foldersByPaths]);

  return (
    <div
      className="flex cursor-default flex-col items-center rounded-[12px] bg-[#121212] text-[12px] text-marveri-white"
      data-testid="data-room-content-component"
    >
      <div className="w-full px-[20px]">
        <div className="relative mb-[24px] mt-[20px] flex w-full items-center justify-between">
          {isRenameClientNameActive ? (
            <form
              onSubmit={() => updateClientName(newClientName)}
              data-testid="rename-client-name-form"
              className="flex rounded-[5px] border border-light-border bg-marveri-background"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="text"
                onChange={handleChange}
                value={newClientName ?? ''}
                onKeyDown={handleKeyDown}
                className="rounded-[5px] bg-inherit indent-2 text-[34px] font-medium outline-none"
                ref={renameInputRef}
              />
            </form>
          ) : (
            <div className="flex items-center gap-4">
              <h1
                className={`${!isUserCurrentEditor ? 'pointer-events-none' : ''} cursor-pointer border border-transparent text-[34px] font-medium`}
                onClick={() => setIsRenameClientNameActive(true)}
                data-testid="client-matter-title"
              >
                {!matterName ? clientName : matterName}
              </h1>
              {currentEditor?.email === user.email && currentEditor.email !== createdBy?.email && (
                <div className="h-[22px] rounded-[28px] bg-[#292829] px-2 py-1 text-[10px] font-medium text-N400">
                  Admin
                </div>
              )}
            </div>
          )}
          {(featureFlags.inviteMultiUser || users.length > 1) && (
            <div id="invite-button" className="flex items-center gap-2">
              <div
                className={`${!isUserCurrentEditor ? 'pointer-events-none' : ''} flex h-[34px] w-fit cursor-pointer items-center justify-center gap-[6px] rounded-full border border-N300 p-[12px] hover:bg-[#292829]`}
                onClick={() => setIsInviteModalOpen(true)}
              >
                <img src={InviteUserIcon} />
                <span className="text-[14px] font-medium text-marveri-white tool-resize-2:hidden tool-resize-2-left-panel:hidden">
                  Invite users
                </span>
              </div>
              {users.length > 1 && (
                <span className="text-[10px] font-bold text-N400">
                  {users.length - 1} USERS SHARED
                </span>
              )}
            </div>
          )}
        </div>
        <div
          className="flex w-full items-center justify-between py-[10px]"
          onClick={() => setIsRenameClientNameActive(false)}
        >
          <div className="flex h-[35px] w-full items-center" data-testid="data-room-buttons">
            <div className="mb-[24px] flex gap-1" data-testid="tool-status-container">
              {userSelectedTool.includes('REDLINE') && (
                <RedlineButton
                  filesAreProcessing={redlinesAreProcessing}
                  totalSelectedFiles={checkedFiles.length}
                />
              )}
              {userSelectedTool.includes('RENAME') && (
                <RenameOptions filesAreProcessing={renameIsProcessing} />
              )}
              {userSelectedTool.includes('ORGANIZE') && (
                <DataRoomToolButton
                  name="Organize"
                  icon={OrganizeIcon}
                  toolIdentifier="ORGANIZE"
                  isDisabled={!hasCheckedFiles || organizeIsProcessing || organizeLoading}
                  onClick={organizeCheckedFiles}
                  filesAreProcessing={organizeIsProcessing || organizeLoading}
                />
              )}
            </div>
            <div className="ml-auto flex flex-row gap-1">
              <DataRoomButton
                title="Export Index"
                icon={ExportIcon}
                onClick={downloadAllFilesAsExcel}
                testId="export-files-button"
                isDisabled={false}
              />
              <DownloadButton
                clientMatterName={matterName}
                clientName={clientName}
                clientMatterId={clientMatterId}
                files={checkedFiles}
                filesByPath={dataRoomDownload}
                setAllFilesUnchecked={setAllFilesUnchecked}
                filesAreProcessing={false}
              />
              <DataRoomAddNew
                clientMatterNumber={matterNumber}
                clientNumber={clientNumber}
                clientMatterId={clientMatterId}
                dataRoomFileNames={dataRoomFileNames}
                filesDirectory={filesDirectory}
                lookupKey={lookupKey ?? ''}
              />
            </div>
            {featureFlags.missingDocColumnSelector ? (
              <button
                className="rounded-lg border-light-border p-[10px]"
                onClick={debugReprocessDataroom}
              >
                Reprocess Dataroom
              </button>
            ) : (
              <></>
            )}
          </div>
        </div>
        <div
          className="flex w-full justify-between pl-[8px] pt-[14px] text-[12px]"
          data-testid="data-room-header"
        >
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <input
                checked={isCheckAllActive}
                type="checkbox"
                onChange={setAllFilesChecked}
                className={`size-[12px] cursor-pointer appearance-none rounded-[2px] border border-marveri-white bg-marveri-background bg-contain bg-center bg-no-repeat checked:bg-[url('@/assets/images/check-icon-white.svg')]`}
                data-testid="data-room-header-checkbox"
              />
              <span>Select All</span>
            </div>
            <div
              className={`${isPanelCollapsed ? 'tool-resize-2-left-panel:w-[260px] tool-resize-3:w-[250px]' : 'tool-resize-2:w-[360px] tool-resize-3:w-[300px]'} flex h-[40px] w-[400px] items-center gap-2 rounded-[4px] bg-[#19181A] px-[10px] py-4 text-N400`}
              data-testid="data-room-search"
            >
              <img src={SearchIcon} />
              <input
                onChange={handleSearch}
                value={searchQuery}
                className="w-full bg-[#19181A] text-[14px] font-medium outline-none placeholder:text-N500"
                placeholder="Search file titles, parties, and keywords"
              />
              {isSearchActive && <MarveriIcon icon={ExitIcon} onClick={handleCancelSearch} />}
            </div>
          </div>
          {checkedFileInstances.length > 0 && (
            <div className="flex pr-[22px]">
              <div className="flex gap-2">
                {filteredSelectedFoldersArray.length > 0 && (
                  <span>
                    {filteredSelectedFoldersArray.length} Folder
                    {filteredSelectedFoldersArray.length > 1 ? 's' : ''}
                  </span>
                )}
                <span>
                  {checkedFileInstances.length} File{checkedFileInstances.length > 1 ? 's' : ''}{' '}
                  Selected
                </span>
              </div>
            </div>
          )}
        </div>
        <ScrollableDiv
          containerStyle="h-[calc(100vh-238px)] w-full pr-[8px] mb-2"
          selectedFile={selectedFile}
          newlyCreatedFolder={newlyCreatedFolder}
          setNewlyCreatedFolder={setNewlyCreatedFolder}
          scrollKey="data-room-content"
        >
          <DndContext onDragEnd={handleDragEnd} onDragStart={handleDragStart} sensors={sensors}>
            <DataRoomContentDisplay />
            <DragOverlay dropAnimation={null}>
              <DragOverlayContent />
            </DragOverlay>
          </DndContext>
        </ScrollableDiv>
        {!!selectedFile && !!isViewerOpen && !!selectedFileUrl ? (
          <PdfViewer
            title={selectedFile.displayName}
            originalTitle={selectedFile.name}
            fileUrl={selectedFileUrl}
            missingSignature={selectedFile.missingSignature}
            multipleDocuments={true}
            closePdfViewer={() => setIsViewerOpen(false)}
            navigateBackFunction={goToPreviousDocument}
            navigateForwardFunction={goToNextDocument}
            selectSecondFile={selectSecondFileByName}
            secondSelectedFile={secondSelectedFile}
            secondSelectedFileUrl={secondSelectedFileUrl}
          />
        ) : (
          <></>
        )}
        {!!selectedFile && !!isSpreadsheetViewerOpen && !!selectedFileOriginalUrl ? (
          <SpreadsheetViewer
            title={selectedFile.displayName}
            fileUrl={selectedFileOriginalUrl}
            onClose={() => setIsSpreadsheetViewerOpen(false)}
            onFallbackToPdf={handleSpreadsheetFallbackToPdf}
          />
        ) : (
          <></>
        )}
      </div>
      {isInviteModalOpen && (
        <InviteUserModal
          isInviteModalOpen={isInviteModalOpen}
          setIsInviteModalOpen={setIsInviteModalOpen}
          documentSetName={matterName}
        />
      )}
    </div>
  );
};
